import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Partial / complete configuration state while a variation master is being configured. */
export interface PresidioVariationPreviewState {
  masterSku?: string;
  /**
   * Best-matching (or exact, when complete) variation SKU for price / ATC / availability.
   * Gallery and long-form enrichment stay on the variation master.
   */
  previewSku?: string;
  pending: Record<string, string>;
  selections: { label: string; value: string }[];
  nextAttributeLabel?: string;
  incomplete: boolean;
}

const EMPTY: PresidioVariationPreviewState = {
  pending: {},
  selections: [],
  incomplete: false,
};

/**
 * CUSTOMIZATION: Presidio — shares progressive master-config state between
 * variation select and PDP buy-box so price / ATC / availability track the
 * selected child SKU while the page stays on master enrichment.
 */
@Injectable({ providedIn: 'root' })
export class PresidioVariationPreviewService {
  private readonly stateSubject = new BehaviorSubject<PresidioVariationPreviewState>(EMPTY);

  readonly state$: Observable<PresidioVariationPreviewState> = this.stateSubject.asObservable();

  get snapshot(): PresidioVariationPreviewState {
    return this.stateSubject.value;
  }

  /** True when this master has a fully resolved variation SKU ready for commerce. */
  isCommerceReady(masterSku: string | undefined): boolean {
    if (!masterSku) {
      return false;
    }
    const s = this.stateSubject.value;
    return s.masterSku === masterSku && !s.incomplete && !!s.previewSku;
  }

  /**
   * SKU to purchase / quote / save: selected variation when master config is complete,
   * otherwise the context SKU.
   */
  resolveCommerceSku(contextSku: string | undefined): string | undefined {
    if (this.isCommerceReady(contextSku)) {
      return this.stateSubject.value.previewSku;
    }
    return contextSku;
  }

  update(partial: Partial<PresidioVariationPreviewState>) {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }

  clear(masterSku?: string) {
    if (masterSku && this.stateSubject.value.masterSku && this.stateSubject.value.masterSku !== masterSku) {
      return;
    }
    this.stateSubject.next(EMPTY);
  }

  /**
   * Baymard: guide the shopper to the next incomplete attribute.
   * Scrolls the configure strip into view and focuses the first available chip.
   */
  focusNextIncomplete(root: ParentNode = typeof document !== 'undefined' ? document : undefined): void {
    if (SSR || !root) {
      return;
    }
    const group = root.querySelector('.prs-variation-group.is-incomplete') as HTMLElement | null;
    const target = group ?? (root.querySelector('.prs-pdp-configure') as HTMLElement | null);
    if (!target) {
      return;
    }
    target.classList.add('is-attention');
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const chip = target.querySelector(
      '.prs-variation-chip:not(:disabled):not(.is-unavailable)'
    ) as HTMLButtonElement | null;
    chip?.focus({ preventScroll: true });
    window.setTimeout(() => target.classList.remove('is-attention'), 1800);
  }

  /**
   * After full configuration, bring the price band into view under the sticky
   * header (see scroll-margin-top on .prs-pdp-price-band). Do not focus ATC —
   * that can overshoot and hide the price.
   */
  focusBuyBox(root: ParentNode = typeof document !== 'undefined' ? document : undefined): void {
    if (SSR || !root) {
      return;
    }
    const prices = root.querySelector('[data-testing-id="prs-pdp-prices"]') as HTMLElement | null;
    if (!prices) {
      return;
    }
    prices.classList.add('is-attention');
    prices.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => prices.classList.remove('is-attention'), 1600);
  }
}
